import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { camelToSnakeCase } from 'utils/caseConversion';
import { useDhsFlag } from 'utils/featureFlags';
import {
  DHS_ASSETS_TAB_SEEDS,
  DHS_REGION4_SYMBOLOGY,
  dhsChecklistsForAutomationRules,
  dhsChecklistsForThreats,
  dhsFacilityIdFromResourceNotes,
  dhsRegion4ResourceNotesMarker,
  formatDhsFacilityLabel,
  isDhsRegion4ResourceSet,
  type DhsAssetsTabSeed,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/dhsRegion4ThreatNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  keys as resourceSetKeys,
  mapResourceSetToEditPayload,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';

const DHS_RESOURCE_KIND = 'Facilities';
const DHS_RESOURCE_TYPE = 'DHS Facility';

function checklistItemsMatch(
  actual: OrgEventChecklistItemDef[] | undefined,
  expected: OrgEventChecklistItemDef[]
): boolean {
  if (!Array.isArray(actual) || actual.length !== expected.length) {
    return false;
  }
  return expected.every((item, index) => {
    const current = actual[index];
    return (
      current?.id === item.id &&
      current?.kind === item.kind &&
      current?.label === item.label
    );
  });
}

function seedMatchesResource(
  resource: ResourceSet,
  seed: DhsAssetsTabSeed
): boolean {
  const threats = Array.isArray(resource.threats) ? resource.threats : [];
  if (
    threats.length !== seed.threats.length ||
    !seed.threats.every((threat) => threats.includes(threat))
  ) {
    return false;
  }
  const expectedThreatChecklists = dhsChecklistsForThreats(seed.threats);
  const expectedRuleChecklists = dhsChecklistsForAutomationRules(seed.threats);
  const ruleNames = expectedRuleChecklists.automationRules;
  const storedRules = Array.isArray(resource.automation_rules)
    ? resource.automation_rules
    : [];
  if (
    storedRules.length !== ruleNames.length ||
    !ruleNames.every((name) => storedRules.includes(name))
  ) {
    return false;
  }
  const threatsMatch = seed.threats.every((threat) =>
    checklistItemsMatch(
      resource.threat_checklists?.[threat],
      expectedThreatChecklists[threat] ?? []
    )
  );
  const rulesMatch = ruleNames.every((name) =>
    checklistItemsMatch(
      resource.automation_rule_checklists?.[name],
      expectedRuleChecklists.automationRuleChecklists[name] ?? []
    )
  );
  return threatsMatch && rulesMatch;
}

const ensureLocks = new Map<string, Promise<unknown>>();

async function patchStaleDhsSeeds({
  resources,
  groupGuid,
  userGuid,
}: {
  resources: ResourceSet[];
  groupGuid: string;
  userGuid: string;
}): Promise<boolean> {
  const seedsById = new Map(
    DHS_ASSETS_TAB_SEEDS.map((seed) => [seed.id, seed])
  );
  const stale = resources.filter((resource) => {
    if (!isDhsRegion4ResourceSet(resource)) return false;
    const seed = seedsById.get(
      dhsFacilityIdFromResourceNotes(resource.notes) ?? ''
    );
    if (!seed) return !resource.symbology;
    return !resource.symbology || !seedMatchesResource(resource, seed);
  });
  if (stale.length === 0) return false;

  await Promise.all(
    stale.map((resource) => {
      const seed = seedsById.get(
        dhsFacilityIdFromResourceNotes(resource.notes) ?? ''
      );
      const threats = seed?.threats ?? resource.threats ?? [];
      const ruleSeed = seed
        ? dhsChecklistsForAutomationRules(seed.threats)
        : {
            automationRules: resource.automation_rules ?? [],
            automationRuleChecklists: resource.automation_rule_checklists ?? {},
          };
      const threatChecklists = seed
        ? {
            ...(resource.threat_checklists ?? {}),
            ...dhsChecklistsForThreats(seed.threats),
          }
        : (resource.threat_checklists ?? {});
      const automationRuleChecklists = seed
        ? {
            ...(resource.automation_rule_checklists ?? {}),
            ...ruleSeed.automationRuleChecklists,
          }
        : (resource.automation_rule_checklists ?? {});
      const payload = {
        ...mapResourceSetToEditPayload(resource.resource_set_id, resource),
        symbology: resource.symbology || DHS_REGION4_SYMBOLOGY,
        threats,
        threatChecklists,
        automationRules: ruleSeed.automationRules,
        automationRuleChecklists,
      };
      const {
        members: _members,
        threatChecklists: checklists,
        automationRuleChecklists: ruleChecklists,
        ...dataForBackend
      } = payload;
      return axios.put(
        `${config.apiGateway.ics_resource_sets}/${resource.resource_set_id}`,
        {
          ...camelToSnakeCase(dataForBackend),
          threat_checklists: checklists,
          automation_rule_checklists: ruleChecklists,
          user_guid: userGuid,
          group_guid: groupGuid,
          incident_id: null,
        }
      );
    })
  );
  return true;
}

async function ensureDhsRegion4ResourceSetsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<ResourceSet[]>(
    `${config.apiGateway.ics_resource_sets}/${groupGuid}`
  );
  const existing = resp.data ?? [];
  const existingMarkers = new Set(
    existing
      .filter(isDhsRegion4ResourceSet)
      .map((resource) => resource.notes as string)
  );

  const toCreate = DHS_ASSETS_TAB_SEEDS.filter(
    (facility) =>
      !existingMarkers.has(dhsRegion4ResourceNotesMarker(facility.id))
  );

  let didMutate = false;

  if (toCreate.length > 0) {
    await Promise.all(
      toCreate.map((facility) => {
        const notes = dhsRegion4ResourceNotesMarker(facility.id);
        const inCone = facility.inHeleneCone === true;
        return axios.post(config.apiGateway.ics_resource_sets, {
          name: formatDhsFacilityLabel(facility),
          description: inCone
            ? `${formatDhsFacilityLabel(facility)} — DHS facility inside the Helene forecast cone.`
            : `${formatDhsFacilityLabel(facility)} — DHS facility.`,
          kind: DHS_RESOURCE_KIND,
          type: DHS_RESOURCE_TYPE,
          quantity: 1,
          status: 'In Organization',
          asset_status: 'PMC',
          asset_status_timestamp: new Date().toISOString(),
          address: `${facility.city}, ${facility.state}`,
          symbology: DHS_REGION4_SYMBOLOGY,
          threats: facility.threats,
          threat_checklists: dhsChecklistsForThreats(facility.threats),
          automation_rules: dhsChecklistsForAutomationRules(facility.threats)
            .automationRules,
          automation_rule_checklists: dhsChecklistsForAutomationRules(
            facility.threats
          ).automationRuleChecklists,
          notes,
          geometry: {
            type: 'Point',
            coordinates: [facility.lon, facility.lat],
          },
          incidents: [],
          archived: false,
          group_guid: groupGuid,
          user_guid: userGuid,
          incident_id: null,
        });
      })
    );
    didMutate = true;
  }

  const patched = await patchStaleDhsSeeds({
    resources: existing,
    groupGuid,
    userGuid,
  });
  if (patched) didMutate = true;

  if (didMutate) {
    await queryClient.invalidateQueries({
      queryKey: resourceSetKeys.all,
    });
  }
}

export function useEnsureDhsRegion4ResourceSets() {
  const dhsEnabled = useDhsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!dhsEnabled || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureDhsRegion4ResourceSetsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error('[DHS] Failed to ensure Region 4 resource sets', error);
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [dhsEnabled, groupGuid, userGuid, queryClient]);
}
